import React from "react";
import update from "immutability-helper";
import { Row, Col, Input, Button, Spin, Form } from "antd";
import {
  LoadingOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import OtpInput from "react-otp-input";

import { connectAutoDispatch } from "../../redux/configureStore";
import {
  registerInitRequest,
  registerVerifyRequest,
  registerCreateUserRequest,
  registerSetError,
} from "../../redux/actions";
import {
  apiCheckCoupon,
  apiCheckoutWithCode,
  apiTrackingUser,
  apiEnableBulkscanProBeta,
} from "../../api/register";
import logoAZInsight from "../../static/images/azinsight.png";
import logoAZAlert from "../../static/images/azalert.png";
import logoBulkChecker from "../../static/images/bulkchecker.png";
import "./index.scss";

const EMAIL_REGEX = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/;
const NAME_REGEX = /^[a-zA-Z\s]+$/;

const ERROR_MESSAGES = {
  USER_EXISTS: (
    <span>
      An account with this email already exists. If you forgot your password
      please reset it. If this error continues,{" "}
      <a target="_blank" href="https://support.asinzen.com/">
        contact support
      </a>
    </span>
  ),
  VERIFICATION_CODE_NOT_CORRECT: (
    <span>
      Verification code entered does not match what was emailed to you. Please
      check and retry. If this error continues,{" "}
      <a target="_blank" href="https://support.asinzen.com/">
        contact support
      </a>
    </span>
  ),
  CONTACT_NOT_FOUND: (
    <span>
      An error occurred during this process,{" "}
      <a target="_blank" href="https://support.asinzen.com/">
        contact support
      </a>
    </span>
  ),
  BLOCKED_EMAIL:
    "Sorry you can not register using this email. We suggest you use a work email or an email from gmail, yahoo, hotmail or any other major email provider.",
  BSP_CONFIG_EXISTS:
    "We found an active account with your email address on our system. Click the button below to enable Bulk Scan Pro Beta on your account.",
  BSP_USER_EXISTS:
    "We found an active account with your email address on our system that has not completed the Amazon API Integration process. Click the button to complete the Integration process.",
  ERROR_502:
    "Error-502: An error occurred communicating with the asinzen server. Please click here to refresh the page. If this error continues please contact support.",
};

class AZInsightForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      verificationCode: "",
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
      couponCode: "",
      showPassword: false,
      showConfirmPassword: false,
      couponCodeStatus: "UNCHECK",
      formValid: false,
      formError: {
        email: true,
        firstName: true,
        lastName: true,
        password: true,
        confirmPassword: true,
        passwordMatch: false,
      },
      enablingBspBeta: false,
    };
  }

  handleInit = () => {
    const { email } = this.state;
    const { freeTrial, type } = this.props;
    this.props.registerInitRequest({ email, freeTrial, registerFrom: type });
  };

  handleVerify = () => {
    const { contactId } = this.props;
    const { verificationCode } = this.state;
    this.props.registerVerifyRequest({ contactId, verificationCode });
  };

  handleCreateUser = () => {
    const { contactId } = this.props;
    const { firstName, lastName, couponCode, password } = this.state;
    this.props.registerCreateUserRequest({
      contactId,
      firstName,
      lastName,
      password,
      couponCode: this.props.lockCouponCode
        ? this.props.couponCode
        : couponCode,
    });
  };

  handleInputChange = (e, key) => {
    const { password, confirmPassword, couponCodeStatus, formError } =
      this.state;
    const value = e.target.value.trim();

    if (key === "couponCode" && this.props.lockCouponCode) {
      return;
    }

    switch (key) {
      case "email":
        formError.email = !EMAIL_REGEX.test(value);
        break;
      case "password":
        formError.password = !value.length;
        formError.passwordMatch = confirmPassword && confirmPassword !== value;
        break;
      case "confirmPassword":
        formError.confirmPassword = !value.length;
        formError.passwordMatch = password !== value;
        break;
      case "firstName":
      case "lastName":
        formError[key] = !value || !NAME_REGEX.test(value);
        break;
      default:
        break;
    }

    const fields = Object.keys(formError);
    let formValid = true;
    for (let i = 0; i < fields.length; i++) {
      if (formError[fields[i]]) {
        formValid = false;
        break;
      }
    }

    this.setState((prevState) =>
      update(prevState, {
        $set: {
          ...prevState,
          [key]: e.target.value,
          formError,
          formValid,
          couponCodeStatus: key === "couponCode" ? "UNCHECK" : couponCodeStatus,
        },
      })
    );
  };

  togglePassord = (key) => {
    this.setState({
      [key]: !this.state[key],
    });
  };

  checkCouponCode = async (e) => {
    e && e.preventDefault();
    try {
      const { contactId, type } = this.props;
      const { couponCode } = this.state;
      this.setState({
        couponCodeStatus: "CHECKING",
      });
      const resp = await apiCheckCoupon({
        contactId,
        couponCode,
        type,
      });
      this.setState({
        couponCodeStatus: resp.status,
      });
    } catch (e) {
      this.setState({
        couponCodeStatus: "UNCHECK",
      });
    }
  };

  enableBspBeta = async () => {
    try {
      this.setState({
        enablingBspBeta: true,
      });
      const { error } = this.props;
      const { accessToken } = error;
      const user = await apiEnableBulkscanProBeta(accessToken);
      window.location.href = `${process.env.REACT_APP_PORTAL}/sp-auth?accessToken=${accessToken}&registerFrom=BULKSCANPRO&contactId=${user.contactId}&email=${user.email}&step=2&state=signup`;
    } catch (e) {
      console.log(e);
      this.setState({
        enablingBspBeta: false,
      });
    }
  };

  completedIntergration = async () => {
    try {
      this.setState({
        enablingBspBeta: true,
      });
      const { error } = this.props;
      const { accessToken } = error;
      const user = await apiEnableBulkscanProBeta(accessToken);
      window.location.href = `${process.env.REACT_APP_PORTAL}/sp-auth?accessToken=${accessToken}&registerFrom=BULKSCANPRO&contactId=${user.contactId}&email=${user.email}&step=1&state=signup`;
    } catch (e) {
      console.log(e);
      this.setState({
        enablingBspBeta: false,
      });
    }
  };

  componentDidMount() {
    const { couponCode, showCouponCode } = this.props;
    this.setState({
      couponCode,
    });
  }

  componentDidUpdate = async (prevProps) => {
    const { contactId, status, type, accessToken } = this.props;
    const { email, couponCode } = this.state;
    if (status !== prevProps.status && status === "SUCCESS") {
      const cid = window.asinzenJs.cookies.get("_az_track") || "123";
      if (cid) {
        try {
          await apiTrackingUser({
            cid,
            type: "signup",
            userEmail: email,
          });
        } catch (e) {
          if ((e.response && e.response.status === 500) || !e.response) {
            this.props.registerSetError({
              code: 502,
              message: "ERROR_502",
            });
            return;
          }
        }
      }
      switch (type) {
        case "AZINSIGHT":
        case "BULKSCANPRO":
          const url = `${
            process.env.REACT_APP_PORTAL
          }/sp-auth?accessToken=${accessToken}&registerFrom=${type}&contactId=${contactId}&email=${email}${
            type === "BULKSCANPRO" ? "&step=1" : ""
          }&state=signup`;
          window.location.href = url;
          break;
        case "AZALERT":
          try {
            const resp = await apiCheckoutWithCode({
              email: this.state.email,
              code: "AZIPALERT",
            });
            window.location.href = resp.url;
          } catch (e) {
            window.location.href =
              "https://goto.asinzen.com/azalert-after-signup";
          }
          break;
        default:
          break;
      }
    }

    if (status !== prevProps.status && status === "VERIFIED" && couponCode) {
      this.checkCouponCode();
    }
  };

  render() {
    const {
      showHeader,
      showHeaderImage,
      showHeaderSteps,
      stepHeaders,
      freeTrial,
      type,
      showCouponCode,
      lockCouponCode,
      status,
      loading,
      error,
    } = this.props;

    let {
      email,
      verificationCode,
      firstName,
      lastName,
      password,
      confirmPassword,
      showPassword,
      showConfirmPassword,
      passwordMatch,
      couponCode,
      couponCodeStatus,
      formError,
      formValid,
      enablingBspBeta,
    } = this.state;
    const validCouponCode =
      !showCouponCode || couponCodeStatus === "VALID" || !couponCode;
    let couponCodeAddon = null;

    switch (couponCodeStatus) {
      case "UNCHECK":
        couponCodeAddon = (
          <span
            onClick={this.checkCouponCode}
            disabled={!couponCode}
            className="ajs-register-form-coupon-status"
            style={{
              backgroundColor: "#1890ff",
            }}
          >
            Apply
          </span>
        );
        break;
      case "CHECKING":
        couponCodeAddon = (
          <span className="ajs-register-form-coupon-status">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />}
            />
          </span>
        );
        break;
      case "VALID":
        couponCodeAddon = (
          <span
            className="ajs-register-form-coupon-status"
            style={{
              backgroundColor: "#52c41a",
            }}
          >
            <CheckCircleOutlined style={{ color: "#fff" }} />
          </span>
        );
        break;
      case "INVALID":
        couponCodeAddon = (
          <span
            className="ajs-register-form-coupon-status"
            style={{
              backgroundColor: "#ff4d4f",
            }}
          >
            <CloseCircleOutlined style={{ color: "#fff" }} />
          </span>
        );
        break;
      default:
        break;
    }

    return (
      <Form className="ajs-register-form" layout="vertical">
        <Row gutter={[8, 8]}>
          {showHeader && status !== "SUCCESS" ? (
            <>
              {showHeaderImage ? (
                <Col span={24} className="ajs-register-form-header">
                  {type === "AZINSIGHT" ? (
                    <img src={logoAZInsight} />
                  ) : type === "AZALERT" ? (
                    <img src={logoAZAlert} />
                  ) : type === "BULKSCANPRO" ? (
                    <img src={logoBulkChecker} />
                  ) : null}
                </Col>
              ) : null}
              {showHeaderSteps ? (
                <>
                  {status === "INIT" && stepHeaders["step1"] ? (
                    <Col span={24} className="ajs-register-form-header-title">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: stepHeaders["step1"],
                        }}
                      ></div>
                    </Col>
                  ) : null}
                  {status === "NOT_VERIFIED" && stepHeaders["step2"] ? (
                    <Col span={24} className="ajs-register-form-header-title">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: stepHeaders["step2"],
                        }}
                      ></div>
                    </Col>
                  ) : null}
                  {status === "VERIFIED" && stepHeaders["step3"] ? (
                    <Col span={24} className="ajs-register-form-header-title">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: stepHeaders["step3"],
                        }}
                      ></div>
                    </Col>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
          {status === "INIT" ? (
            <>
              <Col span={24}>
                <strong>Type your email address to begin</strong>
              </Col>
              <Col span={24} style={{ marginBottom: 20 }}>
                <Input
                  disabled={loading}
                  placeholder="Email"
                  value={email}
                  onChange={(e) => this.handleInputChange(e, "email")}
                />
                {/* {formError.email ? (
                  <div className="ant-form-item-explain-error">
                    <span>Please enter a valid email</span>
                  </div>
                ) : null} */}
              </Col>
              <Col span={24}>
                By signing up, you agree to our{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://asinzen.com/terms"
                >
                  terms
                </a>{" "}
                and{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://asinzen.com/privacy-policy"
                >
                  privacy policy
                </a>
              </Col>
              <Col span={24}>
                <Button
                  block
                  type="primary"
                  htmlType="button"
                  onClick={this.handleInit}
                  disabled={loading || formError.email}
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 18 }} spin />
                      }
                    />
                  ) : (
                    "Get Started"
                  )}
                </Button>
              </Col>
            </>
          ) : status === "NOT_VERIFIED" ? (
            <>
              <Col span={24}>
                <span>
                  An email with a verification code has just been sent to your
                  email address. Please enter the code below to continue.
                </span>
              </Col>
              <Col span={24} style={{ marginBottom: 20 }} className="ajs-otp-input">
                <OtpInput
                  disabled={loading}
                  value={verificationCode}
                  onChange={(value) =>
                    this.handleInputChange(
                      { target: { value } },
                      "verificationCode"
                    )
                  }
                  numInputs={6}
                  renderSeparator={<span></span>}
                  renderInput={(props) => <Input {...props} />}
                />
              </Col>
              <Col span={24}>
                <Button
                  block
                  type="primary"
                  htmlType="button"
                  disabled={loading}
                  onClick={this.handleVerify}
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 18 }} spin />
                      }
                    />
                  ) : (
                    "Verify"
                  )}
                </Button>
              </Col>
            </>
          ) : status === "VERIFIED" ? (
            <>
              <Col span={24}>
                <Form.Item label="Email">
                  <Input disabled={true} value={email} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="First Name">
                  <Input
                    className={formError.firstName ? "ant-input-has-error" : ""}
                    placeholder="First Name"
                    disabled={loading}
                    value={firstName}
                    onChange={(e) => this.handleInputChange(e, "firstName")}
                  />
                </Form.Item>
                {formError.firstName ? (
                  <div className="ant-form-item-explain-error">
                    <span>Please enter a first name</span>
                  </div>
                ) : null}
              </Col>
              <Col span={24}>
                <Form.Item label="Last Name">
                  <Input
                    className={formError.lastName ? "ant-input-has-error" : ""}
                    placeholder="Last name"
                    disabled={loading}
                    value={lastName}
                    onChange={(e) => this.handleInputChange(e, "lastName")}
                  />
                </Form.Item>
                {formError.lastName ? (
                  <div className="ant-form-item-explain-error">
                    <span>Please enter a last name</span>
                  </div>
                ) : null}
              </Col>
              <Col span={24}>
                <Form.Item label="Password">
                  <Input
                    className={formError.password ? "ant-input-has-error" : ""}
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                    disabled={loading}
                    value={password}
                    onChange={(e) => this.handleInputChange(e, "password")}
                    addonAfter={
                      <span
                        className="ajs-pointer"
                        onClick={() => this.togglePassord("showPassword")}
                      >
                        {showPassword ? (
                          <EyeInvisibleOutlined />
                        ) : (
                          <EyeOutlined />
                        )}
                      </span>
                    }
                  />
                  {formError.password ? (
                    <div className="ant-form-item-explain-error">
                      <span>Please enter a password</span>
                    </div>
                  ) : null}
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Confirm Password">
                  <Input
                    className={
                      formError.confirmPassword || formError.passwordMatch
                        ? "ant-input-has-error"
                        : ""
                    }
                    placeholder="Confirm password"
                    type={showConfirmPassword ? "text" : "password"}
                    disabled={loading}
                    value={confirmPassword}
                    onChange={(e) =>
                      this.handleInputChange(e, "confirmPassword")
                    }
                    addonAfter={
                      <span
                        className="ajs-pointer"
                        onClick={() =>
                          this.togglePassord("showConfirmPassword")
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeInvisibleOutlined />
                        ) : (
                          <EyeOutlined />
                        )}
                      </span>
                    }
                  />
                  {formError.confirmPassword ? (
                    <div className="ant-form-item-explain-error">
                      <span>Please enter a confirmation password</span>
                    </div>
                  ) : null}
                  {formError.passwordMatch ? (
                    <div className="ant-form-item-explain-error">
                      <span>
                        Password and password confirmation does not match
                      </span>
                    </div>
                  ) : null}
                </Form.Item>
              </Col>
              {showCouponCode ? (
                <Col span={24}>
                  <Form.Item
                    label="Add promo or coupon code"
                    className="ajs-register-form-coupon"
                  >
                    <Input
                      className={
                        couponCodeStatus === "INVALID"
                          ? "ant-input-has-error"
                          : couponCodeStatus === "VALID"
                          ? "ant-input-has-success"
                          : ""
                      }
                      placeholder="Promo or coupon code"
                      disabled={
                        lockCouponCode || couponCodeStatus === "CHECKING"
                      }
                      value={couponCode}
                      onChange={(e) => this.handleInputChange(e, "couponCode")}
                      addonAfter={couponCodeAddon}
                    />
                    {/* {couponCodeAddon}
                    </Input.Group> */}

                    {couponCodeStatus === "INVALID" ? (
                      <div className="ant-form-item-explain-error">
                        <span>The coupon code is invalid</span>
                      </div>
                    ) : null}
                  </Form.Item>
                </Col>
              ) : null}
              <Col span={24} style={{ marginTop: 20 }}>
                <Button
                  block
                  disabled={loading || !formValid}
                  type="primary"
                  htmlType="button"
                  onClick={this.handleCreateUser}
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 18 }} spin />
                      }
                    />
                  ) : (
                    "GET STARTED NOW"
                  )}
                </Button>
              </Col>
            </>
          ) : status === "SUCCESS" && !error ? (
            <em>Please wait...</em>
          ) : null}
          {error ? (
            <Col span={24}>
              <div role="alert" style={{ color: "rgb(255, 77, 79)" }}>
                <span>
                  {error.message && ERROR_MESSAGES[error.message]
                    ? ERROR_MESSAGES[error.message]
                    : "Something went wrong. Please contact support"}
                </span>
              </div>
              {error.message === "BSP_CONFIG_EXISTS" ? (
                <a
                  onClick={this.enableBspBeta}
                  className="ant-btn ant-btn-primary ant-btn-background-ghost bsp-btn"
                  style={{ width: 275 }}
                  disabled={enablingBspBeta}
                >
                  {enablingBspBeta ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 18 }} spin />
                      }
                    />
                  ) : (
                    "Enable Beta Bulk Scan Pro"
                  )}
                </a>
              ) : error.message === "BSP_USER_EXISTS" ? (
                <a
                  onClick={this.completedIntergration}
                  className="ant-btn ant-btn-primary ant-btn-background-ghost bsp-btn"
                  style={{ width: 275 }}
                  disabled={enablingBspBeta}
                >
                  {enablingBspBeta ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 18 }} spin />
                      }
                    />
                  ) : (
                    "Complete the API Integration Process"
                  )}
                </a>
              ) : null}
            </Col>
          ) : null}
        </Row>
      </Form>
    );
  }
}

export default connectAutoDispatch(
  ({ formState, registerState }) => ({
    formState,
    ...registerState,
  }),
  {
    registerInitRequest,
    registerVerifyRequest,
    registerCreateUserRequest,
    registerSetError,
  }
)(AZInsightForm);
